import React, { ReactNode, cloneElement, useState } from 'react';
import styles from './SelectableWidgetsContainer.module.scss';
import cn from 'classnames';
import { WidgetContainerProps } from './WidgetContainer';
import { SlideIn } from './animations/SlideIn';
export interface SelectableWidgetsContainerProps {
  /**
   * Array of widget elements to render
   */
  children: ReactNode;
  /**
   * Layout direction for the widgets
   * @default 'horizontal'
   */
  direction?: 'horizontal' | 'vertical';
  /**
   * Additional CSS class name
   */
  className?: string;
  /** Starting index for animations */
  startIndex?: number;
  /** Whether to hide the unselected children */
  hideUnselected?: boolean;
}

/**
 * Container component that displays an array of widgets with 15px spacing
 * and automatically marks each widget as selectable
 */
export default function SelectableWidgetsContainer({
  children,
  direction = 'horizontal',
  className,
  startIndex,
  hideUnselected,
}: SelectableWidgetsContainerProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const selectableChildren = React.Children.map(children, (child, index) => {
    if (React.isValidElement<WidgetContainerProps>(child)) {
      const isSelected = selectedIndex === index;
      const isUnselected = hideUnselected && selectedIndex !== null && selectedIndex !== index;

      // Get onSelect prop from child in order to augment it
      const originalOnSelect = child.props.onSelect;

      const clonedElement = cloneElement(child, {
        isSelectable: true,
        isSelected,
        isUnselected,
        onSelect: () => {
          setSelectedIndex(index);
          originalOnSelect?.();
        },
      });

      if (startIndex !== undefined && index >= startIndex) {
        return clonedElement;
      }

      return (
        <SlideIn key={index} delay={index * 100} duration={2000}>
          {clonedElement}
        </SlideIn>
      );
    }

    console.error('Child of SelectableWidgetsContainer is not a valid WidgetContainer:', child);

    return child;
  });

  return (
    <div
      className={cn(
        styles.selectableWidgetsContainer,
        styles[direction],
        className
      )}
    >
      {selectableChildren}
    </div>
  );
}
